import { nativeShell } from "@/platform"
import { useRouterState } from "@tanstack/react-router"
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react"

function isProjectMapRoute(pathname: string) {
  return /^\/projects\/[^/]+\/?$/.test(pathname)
}

function subscribeToThemeClass(onStoreChange: () => void) {
  const observer = new MutationObserver(onStoreChange)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  })
  return () => observer.disconnect()
}

function getThemeClassSnapshot() {
  return document.documentElement.classList.contains("dark")
}

export function CapacitorStatusBar() {
  const applySequenceRef = useRef(0)
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const dark = useSyncExternalStore(
    subscribeToThemeClass,
    getThemeClassSnapshot,
    getThemeClassSnapshot,
  )

  const shouldOverlayMap = isProjectMapRoute(pathname)

  const applyStatusBar = useCallback(async () => {
    const sequence = ++applySequenceRef.current
    await nativeShell.applyStatusBar({ dark, shouldOverlayMap })
    if (sequence !== applySequenceRef.current) return
  }, [dark, shouldOverlayMap])

  useEffect(() => {
    void applyStatusBar()
    const id = window.setTimeout(() => void applyStatusBar(), 0)
    return () => window.clearTimeout(id)
  }, [applyStatusBar])

  useEffect(() => {
    const restore = () => void applyStatusBar()
    const unsubscribeStatusBarRestore =
      nativeShell.subscribeStatusBarRestore(restore)
    window.addEventListener("focus", restore)
    document.addEventListener("visibilitychange", restore)
    return () => {
      unsubscribeStatusBarRestore()
      window.removeEventListener("focus", restore)
      document.removeEventListener("visibilitychange", restore)
    }
  }, [applyStatusBar])

  return null
}
